import { useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { getToken, hubFetch } from "../lib/auth.js";

/**
 * 发布 skill 弹窗。
 *
 * 流程:
 *   1. 用户选 zip 文件(dnd 或点击)
 *   2. 浏览器 Web Crypto 算 sha256
 *   3. POST /admin/upload-direct(body=文件,headers=元数据)→ 拿 storageId
 *   4. POST /api/v1/skills(清单+元数据)→ 落库
 *   5. 关闭 + invalidate packages query
 *
 * 不用 presign URL —— 本地 dev 没真实 R2 S3 凭据,直接 binding.put 最快。
 */
export function PublishSkillModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState("");
  const [version, setVersion] = useState("0.1.0");
  const [displayName, setDisplayName] = useState("");
  const [description, setDescription] = useState("");
  const [channel, setChannel] = useState<"official" | "community" | "private">(
    "official",
  );
  const [changelog, setChangelog] = useState("初始发布");
  const [tags, setTags] = useState("");
  // 默认不纳入管控 — 上架不等于受平台管控,发布后想管再单独 toggle 也行
  const [managed, setManaged] = useState(false);
  const [busy, setBusy] = useState(false);
  const [stage, setStage] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handlePickFile = (f: File) => {
    setFile(f);
    if (!name) {
      // 从文件名推断 name/version:my-skill-1.0.0.zip → my-skill / 1.0.0
      const base = f.name.replace(/\.(zip|tar\.gz|tgz)$/i, "");
      const m = base.match(/^(.+?)-(\d[\d.\w-]*)$/);
      if (m) {
        setName(m[1]!);
        setVersion(m[2]!);
      } else {
        setName(base);
      }
    }
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) {
      setError("请选择一个 zip 文件");
      return;
    }
    if (!name || !version) {
      setError("name 和 version 必填");
      return;
    }

    setBusy(true);
    setError(null);
    try {
      // 1. 读文件 + 算 sha256
      setStage("读取文件...");
      const buf = await file.arrayBuffer();
      const hashBuf = await crypto.subtle.digest("SHA-256", buf);
      const sha256 = [...new Uint8Array(hashBuf)]
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("");

      // 2. 上传到 R2
      setStage(`上传 ${(buf.byteLength / 1024).toFixed(1)} KB...`);
      const uploadRes = await fetch(
        `${(import.meta.env.VITE_HUB_API_BASE as string | undefined) ?? "http://localhost:8787"}/admin/upload-direct`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${getToken()}`,
            "Content-Type": file.type || "application/zip",
            "X-Mhwork-Package-Name": name,
            "X-Mhwork-Package-Version": version,
            "X-Mhwork-File-Path": file.name,
            "X-Mhwork-Sha256": sha256,
          },
          body: buf,
        },
      );
      if (!uploadRes.ok) {
        const text = await uploadRes.text();
        throw new Error(`上传失败: ${text}`);
      }
      const upload = (await uploadRes.json()) as {
        storageId: string;
        size: number;
        sha256: string;
      };

      // 3. 发布清单
      setStage("登记元数据...");
      await hubFetch<{ name: string; version: string }>("/api/v1/skills", {
        method: "POST",
        body: JSON.stringify({
          name,
          family: "skill",
          version,
          changelog,
          displayName: displayName || undefined,
          description: description || undefined,
          channel,
          tags: tags
            ? tags
                .split(",")
                .map((t) => t.trim())
                .filter(Boolean)
            : undefined,
          files: [
            {
              path: file.name,
              size: upload.size,
              storageId: upload.storageId,
              sha256: upload.sha256,
              contentType: file.type || "application/zip",
            },
          ],
        }),
      });

      // managed 不在 ClawHub v1 publish schema 里(协议兼容用),
      // 用户勾了的话发布后再 PATCH 一次切上去。失败不阻断关弹窗,
      // 用户能从列表的"管控"按钮再次操作。
      if (managed) {
        try {
          setStage("纳入管控...");
          await hubFetch(`/admin/packages/${encodeURIComponent(name)}`, {
            method: "PATCH",
            body: JSON.stringify({ managed: true }),
          });
        } catch (e) {
          console.warn("[publish] managed toggle failed, can be retried:", e);
        }
      }

      setStage("完成");
      qc.invalidateQueries({ queryKey: ["admin", "packages"] });
      onClose();
    } catch (err) {
      const raw = err instanceof Error ? err.message : "发布失败";
      // 友好化常见错误:
      // 409 already published → 版本冲突,引导改版本号
      // 401 / 403 → 权限
      // 413 → 文件过大
      let friendly = raw;
      if (raw.includes("already published")) {
        friendly = `版本 ${version} 已经发布过,不能重复上传同版本。请把 version 改成更高的号(比如 ${bumpVersion(version)})。`;
      } else if (raw.includes("403")) {
        friendly = "你的角色没有发布权限(需要 publisher 或 owner)。";
      } else if (raw.includes("413")) {
        friendly = "文件过大,单包最多 50MB。";
      } else if (raw.includes("sha256 mismatch")) {
        friendly = "上传校验失败,文件可能在传输中损坏,请重试。";
      }
      setError(friendly);
    } finally {
      setBusy(false);
      setStage("");
    }
  };

  return (
    <div style={styles.mask} onClick={busy ? undefined : onClose}>
      <form
        style={styles.modal}
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
      >
        <h2 style={styles.title}>发布新 skill</h2>

        {/* 文件选择 */}
        <div
          style={{
            ...styles.drop,
            ...(file ? styles.dropHasFile : {}),
          }}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
          }}
          onDrop={(e) => {
            e.preventDefault();
            const f = e.dataTransfer.files[0];
            if (f) handlePickFile(f);
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".zip,.tar.gz,.tgz,application/zip"
            style={{ display: "none" }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handlePickFile(f);
            }}
          />
          {file ? (
            <>
              <div style={{ fontSize: 13, fontWeight: 500 }}>📦 {file.name}</div>
              <div style={{ fontSize: 11, color: "#6e6e73", marginTop: 4 }}>
                {(file.size / 1024).toFixed(1)} KB · 点击替换
              </div>
            </>
          ) : (
            <>
              <div style={{ fontSize: 13 }}>点击选择 .zip 文件,或拖进来</div>
              <div style={{ fontSize: 11, color: "#6e6e73", marginTop: 4 }}>
                也接受 .tar.gz / .tgz,单文件上限 50MB
              </div>
            </>
          )}
        </div>

        <div style={styles.row2}>
          <label style={styles.label}>
            name
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="my-skill"
              style={styles.input}
              required
              pattern="[a-z0-9][a-z0-9\-_]{0,127}"
              title="小写字母数字 / 连字符 / 下划线"
            />
          </label>
          <label style={styles.label}>
            version
            <input
              value={version}
              onChange={(e) => setVersion(e.target.value)}
              placeholder="0.1.0"
              style={styles.input}
              required
            />
          </label>
        </div>

        <label style={styles.label}>
          显示名(可选)
          <input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="供应商询价"
            style={styles.input}
          />
        </label>

        <label style={styles.label}>
          描述(可选)
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            placeholder="一句话简短描述 skill 的用途(会在卡片里显示两行)"
            style={{ ...styles.input, height: "auto", padding: 8, resize: "vertical", fontFamily: "inherit" }}
          />
        </label>

        <div style={styles.row2}>
          <label style={styles.label}>
            channel
            <select
              value={channel}
              onChange={(e) => setChannel(e.target.value as typeof channel)}
              style={styles.input}
            >
              <option value="official">official — 官方出品</option>
              <option value="community">community — 社区/开源</option>
              <option value="private">private — 企业私有</option>
            </select>
          </label>
          <label style={styles.label}>
            tags(逗号分隔)
            <input
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder="办公, 文档"
              style={styles.input}
            />
          </label>
        </div>

        <label style={styles.label}>
          changelog
          <textarea
            value={changelog}
            onChange={(e) => setChangelog(e.target.value)}
            rows={3}
            style={{ ...styles.input, height: "auto", padding: 8, resize: "vertical" }}
          />
        </label>

        {/* 远程管控开关 — 默认不勾 */}
        <label
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 10,
            padding: 10,
            border: "1px solid var(--border)",
            borderRadius: 8,
            cursor: "pointer",
          }}
        >
          <input
            type="checkbox"
            checked={managed}
            onChange={(e) => setManaged(e.target.checked)}
            style={{ marginTop: 3 }}
          />
          <div>
            <div style={{ fontSize: 13, fontWeight: 500 }}>纳入 mhwork 平台远程管控</div>
            <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 4 }}>
              开启后客户端会拉到这个 skill 的策略,事故时可远程停用 / 下架。
              不勾则与普通广场 skill 一样,平台不替作者扛事故。
              发布后也可在列表里切换。
            </div>
          </div>
        </label>

        {error && <div style={styles.err}>{error}</div>}
        {busy && <div style={styles.busy}>{stage}</div>}

        <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            style={styles.btnCancel}
          >
            取消
          </button>
          <button type="submit" disabled={busy || !file} style={styles.btnSubmit}>
            {busy ? "发布中…" : "发布"}
          </button>
        </div>
      </form>
    </div>
  );
}

/** 建议下一个版本号:1.0.2 → 1.0.3, 1.2 → 1.3, 其他保留 */
function bumpVersion(v: string): string {
  const m = v.match(/^(.*?)(\d+)(\D*)$/);
  if (!m) return `${v}.1`;
  return `${m[1]}${Number(m[2]) + 1}${m[3]}`;
}

const styles: Record<string, React.CSSProperties> = {
  mask: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.4)",
    display: "grid",
    placeItems: "center",
    zIndex: 100,
  },
  modal: {
    width: 460,
    maxHeight: "90vh",
    overflowY: "auto",
    background: "#fff",
    borderRadius: 12,
    padding: 24,
    display: "flex",
    flexDirection: "column",
    gap: 12,
    boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
  },
  title: { fontSize: 17, margin: 0, fontWeight: 600 },
  drop: {
    border: "2px dashed #d2d2d7",
    borderRadius: 10,
    padding: "24px 16px",
    textAlign: "center",
    cursor: "pointer",
    background: "#fafafa",
    transition: "all .15s",
  },
  dropHasFile: {
    border: "2px solid #0071e3",
    background: "#f0f7ff",
  },
  row2: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 },
  label: { display: "flex", flexDirection: "column", gap: 4, fontSize: 12 },
  input: {
    height: 34,
    padding: "0 10px",
    borderRadius: 6,
    border: "1px solid #d2d2d7",
    fontSize: 13,
    fontFamily: "inherit",
  },
  err: {
    padding: "8px 10px",
    background: "#fce8e6",
    color: "#d93025",
    fontSize: 12,
    borderRadius: 4,
  },
  busy: {
    padding: "8px 10px",
    background: "#e3f2fd",
    color: "#0050b3",
    fontSize: 12,
    borderRadius: 4,
  },
  btnCancel: {
    flex: 1,
    height: 36,
    borderRadius: 6,
    border: "1px solid #d2d2d7",
    background: "#fff",
    cursor: "pointer",
    fontSize: 13,
  },
  btnSubmit: {
    flex: 2,
    height: 36,
    borderRadius: 6,
    border: 0,
    background: "#1d1d1f",
    color: "#fff",
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 500,
  },
};
